import React, { useEffect, useState } from "react";
import api from "../../api";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

const TokenUsageChart = () => {
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [themeColors, setThemeColors] = useState({});

  // 🎨 Load theme colors from CSS variables
  const loadThemeColors = () => {
    const styles = getComputedStyle(document.documentElement);
    return {
      text: styles.getPropertyValue("--dark-color").trim(),
      grid: styles.getPropertyValue("--border-color").trim(),
      bg: styles.getPropertyValue("--background-color").trim(),
    };
  };

const parseStorageValue = (value) => {
  if (!value) return 0;
  if (typeof value === "number") return value;

  const match = value.match(/([\d.]+)\s*(KB|MB|GB)?/i);
  if (!match) return 0;

  const size = parseFloat(match[1]);
  const unit = match[2]?.toUpperCase() || "KB";

  switch (unit) {
    case "GB":
      return size * 1024; // GB → MB
    case "MB":
      return size;        // MB as MB
    case "KB":
    default:
      return size / 1024; // KB → MB
  }
};


  // 🌓 Watch for theme changes
  useEffect(() => {
    setThemeColors(loadThemeColors());

    const observer = new MutationObserver(() => {
      setThemeColors(loadThemeColors());
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  // 📊 Fetch API usage + storage usage data
  useEffect(() => {
    const fetchMonthlyUsage = async () => {
      try {
        const token =
          localStorage.getItem("access_token") ||
          sessionStorage.getItem("access_token");
        const baseUrl = process.env.REACT_APP_API_URL;

        const response = await api.get(`${baseUrl}/chat/monthly-usage`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const monthlyStats = response.data.monthlyStats || [];
        const labels = monthlyStats.map((item) => item.month);

        const storageMB = monthlyStats.map((item) =>
          parseStorageValue(item.storageUsed)
        );


        setChartData({
          labels,
          datasets: [
            {
              label: "API Usage",
              data: monthlyStats.map((item) => item.tokenUsage || 0),
              borderColor: "#3b82f6",
              backgroundColor: "#3b82f6",
              tension: 0.3,
              pointRadius: 4,
              pointHoverRadius: 6,
              fill: false,
              yAxisID: "y1",
            },
            {
              label: "Storage Used",
              data: storageMB,
              borderColor: "#8b5cf6",
              backgroundColor: "#8b5cf6",
              tension: 0.3,
              pointRadius: 4,
              pointHoverRadius: 6,
              fill: false,
              yAxisID: "y2",
            },
          ],
        });
      } catch (error) {
        console.error("❌ Failed to fetch monthly usage:", error);
      }
    };

    fetchMonthlyUsage();
  }, [themeColors]);

  // ⚙️ Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        align: "start",
        labels: {
          usePointStyle: true,
          pointStyle: "line",
          color: themeColors.text,
          font: {
            size: 13,
            weight: "500",
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const datasetLabel = context.dataset.label || "";
            let value = context.parsed.y;

            // if (datasetLabel === "Storage Used") {
            //   if (value >= 1024) value = (value / 1024).toFixed(2) + " GB";
            //   else value = value.toFixed(2) + " MB";
            // }

             if (datasetLabel === "Storage Used") {
        if (value >= 1024) {
          // Show GB
          value = (value / 1024).toFixed(2) + " GB";
        } else if (value >= 1) {
          // Show MB
          value = value.toFixed(2) + " MB";
        } else {
          // Show KB
          value = (value * 1024).toFixed(2) + " KB";
        }
      }

            return `${datasetLabel}: ${value}`;
          },
        },
      },
    },
    scales: {
      y1: {
        beginAtZero: true,
        position: "left",
        title: {
          display: true,
          text: "API Usage (Tokens)",
          color: themeColors.text,
        },
        ticks: {
          color: themeColors.text,
        },
        grid: {
          color: themeColors.grid,
        },
      },
      y2: {
        beginAtZero: true,
        position: "right",
        title: {
          display: true,
          text: "Storage Used",
          color: themeColors.text,
        },
        ticks: {
          color: themeColors.text,
          callback: function (value) {
            if (value >= 1024) return (value / 1024).toFixed(1) + " GB";
            return value.toFixed(1) + " MB";
          },
        },
        grid: {
          drawOnChartArea: false,
        },
      },
      x: {
        ticks: {
          color: themeColors.text,
        },
        grid: {
          color: themeColors.grid,
        },
      },
    },
  };

  return (
    <div className="chart-card p-4 shadow-sm rounded profile mb-5">
      <h4 className="fw-bold mb-0">
        <span className="border-start border-4 ps-2 refresh-prompts">
          Monthly API & Storage Usage
        </span>
      </h4>
      <div style={{ height: "320px", width: "100%" }}>
        <p
          className="fw-bold mt-3 refresh-prompts"
          style={{ marginBottom: "-1px" }}
        >
          Usage Overview
        </p>
        <Line data={chartData} options={options} className="profile" />
      </div>
    </div>
  );
};

export default TokenUsageChart;
